import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { q } from "./api.ts";
import { metricValue } from "./format.ts";
import { DATA_SETUP_LABELS as S, DIAGNOSIS_LABELS as L, METRIC_DASHBOARD_LABELS as D, METRIC_LABELS as M } from "./labels.ts";
import { diagnose, NEED_STATUS, type NeedRow } from "./diagnosis-items.ts";
import { useMetricActions } from "./metric-actions-context.ts";
import { displayUnit, metricBars } from "./metric-cards.tsx";
import { direction, metricName } from "./metric-dashboard.ts";
import { Button, Callout, ErrorText } from "./ui.tsx";
import { GroupHeader, ListRow } from "./ui/list.tsx";
import { MiniBars } from "./ui/measure.tsx";
import { Skeleton } from "./ui/skeleton.tsx";
import { StatusIcon } from "./ui/status.tsx";

// Insights › Diagnosis (docs/design-system.md §0.4f, 2026-11-01): one row per
// number the product's kind needs, grouped by stage in funnel order. On top,
// one thing to do when there is one: connect a required number, or fill one
// without data. A number that moved the wrong way is named in a quiet line,
// never as a cause. A connected row opens the metric's detail over this tab;
// a row not connected yet opens the data setup.

/** The change as the metric cards write it: a direction only for a small sample. */
function changeText(row: NeedRow): string | null {
  const r = row.metric?.range;
  if (!r || r.value === null) return null;
  if (row.small) { const moved = direction(r); return moved && D.direction[moved](r.days); }
  return r.change === null ? null : M.change(r.change, r.change_unit);
}

export function Diagnosis({ productId }: { productId: string }) {
  const setup = useQuery(q.dataSetup(productId));
  const dashboard = useQuery(q.metricDashboard(productId, "4", "week"));
  const actions = useMetricActions();
  if (setup.isLoading || dashboard.isLoading) return <Skeleton shape="list" rows={4} />;
  if (!setup.data || !dashboard.data) return <ErrorText error={setup.error ?? dashboard.error} />;
  const data = dashboard.data;
  const { stages, attention } = diagnose(setup.data, data.metrics);
  const ranged = data.metrics.find((m) => m.range.value !== null) ?? data.metrics[0];
  const setupLink = `/inbox/data/${encodeURIComponent(productId)}`;
  return <div className="z-flow z-diagnosis">
    <p className="z-small z-muted">{[S.profile[setup.data.profile], ranged && L.context(M.range(ranged.range.from, ranged.range.to))].filter(Boolean).join(" · ")}</p>
    {attention?.kind === "connect" && <Callout><div className="z-callout-row">
      <span>{L.connectFirst(attention.missing, L.stage[attention.stage], S.need[attention.row.need] ?? attention.row.need)}</span>
      <Link className="z-btn sm" to={setupLink}>{L.connect}</Link>
    </div></Callout>}
    {attention?.kind === "no_data" && attention.row.metric && <Callout><div className="z-callout-row">
      <span>{L.noData(metricName(attention.row.metric))}{attention.row.metric.missing ? ` ${D.missing[attention.row.metric.missing]}` : ""}</span>
      <Button size="sm" onClick={() => actions.open(attention.row.metric!.definition.key, "quality")}>{L.openMetric}</Button>
    </div></Callout>}
    {attention?.kind === "worse" && attention.row.metric && <p className="z-diagnosis-line"><StatusIcon kind="negative" label="" />{L.worse(metricName(attention.row.metric), changeText(attention.row) ?? "–")}</p>}
    {attention === null && stages.length > 0 && <p className="z-diagnosis-line"><StatusIcon kind="positive" label="" />{L.steady}</p>}
    <div className="z-boxed">{stages.map((stage) => <div key={stage.stage}>
      <GroupHeader label={L.stage[stage.stage]} count={stage.needs.length} status={stage.status} />
      {stage.needs.map((row) => {
        const importance = row.importance === "required" ? S.required : S.recommended;
        const needName = S.need[row.need] ?? row.need;
        if (!row.metric) return <ListRow key={row.need} to={setupLink} status="not_started" title={needName} sub={L.notConnected(importance)} />;
        const m = row.metric, name = metricName(m);
        const change = changeText(row);
        const sub = [name !== needName ? needName : null, m.primary_for.length ? L.testedBy(m.primary_for.map((c) => `“${c.title}”`)) : null, row.state === "unknown" ? L.unknown : null].filter(Boolean).join(" · ");
        const bars = m.points.some((p) => p.value !== null) ? metricBars(m, data.periods) : null;
        return <ListRow key={row.need} to={`/p/${productId}/data`} search={{ metric: m.definition.key }} status={NEED_STATUS[row.state]} title={name} sub={sub || undefined}
            chips={change ? <span className="z-diagnosis-change" data-state={row.small ? "small" : row.state}>{change}</span> : undefined}
            meta={m.range.value === null ? "–" : `${metricValue(m.range.value, m.definition.kind)}${m.definition.kind === "rate" ? "" : ` ${displayUnit(m, m.range.value)}`}`}
            end={bars ? <MiniBars bars={bars} label={`${name}: ${bars.map((b) => b.label).join("; ")}`} /> : undefined} />;
      })}
    </div>)}</div>
    <p className="z-small z-muted">{L.note}</p>
  </div>;
}
